import { initialiseDeleteModal } from '../ts/deleteAlertFromEmail';
import { createSearchBarDropdowns, createWhereCanIGoDropdowns } from './dropdown.js';
import { initialiseDatepickers } from '../ts/datepicker';
import { canSearch, disableSearch } from '../ts/canSearch';
import { reloadSearchHistory, loadPopularSearches, makeSearch } from '../ts/searches';
import { storeLocalStorage } from '../ts/common';
import { searchBarLocalStorage, wcigLocalStorage, searchCount } from './loadLocalStorage.js';
import { clarityEvent } from '../ts/clarityTags';

function init(routeDictionary, defaultAirline, subscriber, airlineSettings, searchHistoryURL, canSearchAvailabilityURL, pricingURL, popularRoutesURL, loggedIn, updatePopularSearches=false, popularSearches=null, isBot=false) {
	window.autosearch = false;
	createSearchBarDropdowns(JSON.parse(JSON.stringify(routeDictionary)), defaultAirline, searchBarLocalStorage.defaultRoutes[defaultAirline].origin, searchBarLocalStorage.defaultRoutes[defaultAirline].destination, null);
	if (subscriber) {
        var airlineSettings = airlineSettings;
        var datepickers = initialiseDatepickers('wcig-out-date', 'wcig-in-date', 28, airlineSettings[wcigLocalStorage.airline].DaysOfAvailability);
        initialiseTripToggleAndDurationRange();
        createWhereCanIGoDropdowns(JSON.parse(JSON.stringify(routeDictionary)), wcigLocalStorage.airline, wcigLocalStorage.airlineAirport[wcigLocalStorage.airline], airlineSettings, datepickers);
    } else {
        document.getElementById('try-search-cta').addEventListener('click', function () {
            window.scroll(0, document.querySelector('form.main-searchbar').offsetTop - 100);
        });
    }
	initialiseHomePage(searchHistoryURL, canSearchAvailabilityURL, pricingURL, popularRoutesURL, loggedIn, subscriber, updatePopularSearches);  // Needs to be after datepicker initialisation
	document.addEventListener('DOMContentLoaded', function () {
		initialiseLogoStickiness();
	});

    // Airline specific landing page code
    if (!updatePopularSearches && popularSearches) {
        const popularSearchesBtn = document.querySelectorAll("#popular-searches .search button");
        for (let i = 0; i < popularSearches.length; i++) {
            popularSearchesBtn[i].addEventListener("click", () => {
                makeSearch(defaultAirline, popularSearches[i].originAirportId, popularSearches[i].destinationAirportId);
            });
        }
    }
}

async function initialiseHomePage(searchHistoryUrl, canSearchUrl, pricingPageUrl, popularSearchesUrl, loggedIn, showWhereCanIGoForm, updatePopularSearches) {
    // Tasks are run in parallel
    let tasks = [];
    // Only check if the user can search and their search history if they are logged in
    if (loggedIn) {
        if (showWhereCanIGoForm) {
            initialiseWhereCanIGoForm();
        }
        let canSearchPromise = new Promise(async function(resolve, reject) {
            await canSearch(canSearchUrl, pricingPageUrl, 0, reject);  // TODO - Get number of searches from cookie
            resolve();
        });
        tasks.push(canSearchPromise)

        let reloadSearchHistoryPromise = new Promise(async function(resolve, reject) {
            await reloadSearchHistory(searchHistoryUrl, reject, 'search-history-template-index');
            resolve();
        });
        tasks.push(reloadSearchHistoryPromise);
    } else if (searchCount.value > 4) {
        // Disable next search
        disableSearch(pricingPageUrl);
    }

    if (updatePopularSearches) {
        let reloadPopularSearchesPromise = new Promise(async function(resolve, reject) {
            await loadPopularSearches(popularSearchesUrl, reject);
            resolve();
        });
        tasks.push(reloadPopularSearchesPromise);
    }

    await Promise.all(tasks).catch((error) => {
        console.error(error);
    });
}

function initialiseLogoStickiness() {
    const logo = document.getElementById('logo');
    const header = document.getElementsByTagName('header')[0];
    var initialOffsetTop = logo.offsetTop;

    function toggleLogoStickiness() {
        if (window.scrollY > initialOffsetTop) {
            logo.classList.add('fixed');
            header.style.background = 'var(--colour-banner)';
        } else {
            logo.classList.remove('fixed');
            header.style.background = 'var(--colour-banner-light-only)';
        }
    }

    window.addEventListener('scroll', toggleLogoStickiness);
    toggleLogoStickiness();
}

function initialiseWhereCanIGoForm() {
    initialiseWhereCanIGoFlightTypeToggle();
    initialiseWhereCanIGoDirectionToggle();
    initialiseDatePickerValues();
}

function initialiseWhereCanIGoFlightTypeToggle() {
    const flightTypeToggles = document.querySelectorAll('input[type=radio][name="flight-type"]');
    const inboundDateRangeInput = document.getElementById('flatpickr-hidden-wcig-in-date')._flatpickr._input;
    const tripDurationInput = document.getElementById('wcig-trip-duration');

    if (wcigLocalStorage['trip-duration']) {
        tripDurationInput.value = wcigLocalStorage['trip-duration'];
    }

    tripDurationInput.addEventListener('input', function(e) {
        wcigLocalStorage['trip-duration'] = e.target.value;
        storeLocalStorage('wcig', JSON.stringify(wcigLocalStorage));
    });

    flightTypeToggles.forEach(function(toggle) {
        if (toggle.value == wcigLocalStorage['flight-type']) {
            toggle.checked = true;
            toggleWhereCanIGoFlightType(toggle.value);
        }
        toggle.addEventListener('change', function(e) {
            toggleWhereCanIGoFlightType(e.target.value)
            wcigLocalStorage['flight-type'] = e.target.value;
            storeLocalStorage('wcig', JSON.stringify(wcigLocalStorage));
        });
    });

    function toggleWhereCanIGoFlightType(flightType) {
        if (flightType == 'one-way') {
            inboundDateRangeInput.setAttribute("disabled", "disabled");
            tripDurationInput.setAttribute("disabled", "disabled");
        } else {
            inboundDateRangeInput.removeAttribute("disabled");
            // Match the state of the toggle button (Disabled for premium users)
            tripDurationInput.disabled = document.getElementById('wcig-trip-duration-toggle').disabled;
        }
    }
}

function initialiseTripToggleAndDurationRange() {
    const returnTypeToggles = document.querySelectorAll('input[type=radio][name="return-type"]');
    const returnTypeOptions = document.getElementById('return-type-options');

    returnTypeToggles.forEach(function(toggle) {
        if (!toggle.disabled && toggle.value == wcigLocalStorage['return-type']) {
            toggle.checked = true;
            toggleWhereCanIGoReturnType(toggle.value);
        }
        toggle.addEventListener('change', function(e) {
            clarityEvent(`wcig-${e.target.value}-clicked`);
            toggleWhereCanIGoReturnType(e.target.value)
            wcigLocalStorage['return-type'] = e.target.value;
            storeLocalStorage('wcig', JSON.stringify(wcigLocalStorage));
        });
    });

    toggleWhereCanIGoReturnType(document.querySelector('input[name="return-type"]:checked').value);

    function toggleWhereCanIGoReturnType(returnType) {
        if (returnType == 'date-range') {
            returnTypeOptions.classList.remove('trip-duration');
            returnTypeOptions.classList.add('date-range');
        } else {
            returnTypeOptions.classList.remove('date-range');
            returnTypeOptions.classList.add('trip-duration');
        }
    }

    const doubleRange = document.getElementById('wcig-trip-duration');
    const doubleRangeVal = document.getElementById('wcig-trip-duration-val');
    updateTripDurationRange(doubleRange.value);
    doubleRange.addEventListener('input', function(e) {
        const rng = this.value;
        updateTripDurationRange(rng);
    });

    function updateTripDurationRange(rng) {
        if (rng[0] == rng[1]) {
            if (rng[0] == 1) {
                doubleRangeVal.textContent = rng[0] + " day";
            } else {
                doubleRangeVal.textContent = rng[0] + " days";
            }
        } else {
            doubleRangeVal.textContent = rng[0] + " - " + rng[1] + " days";
        }
    }
}

function initialiseWhereCanIGoDirectionToggle() {
    const flightTypeDirection = document.querySelectorAll('input[type=radio][name="wcig-direction"]');
    const wcigForm = document.getElementById('where-can-i-go');

    flightTypeDirection.forEach(function(direction) {
        if (direction.value == wcigLocalStorage.direction) {
            direction.checked = true;
            toggleWhereCanIGoDirection(direction.value);
        }
        direction.addEventListener('change', function(e) {
            toggleWhereCanIGoDirection(e.target.value)
            wcigLocalStorage.direction = e.target.value;
            storeLocalStorage('wcig', JSON.stringify(wcigLocalStorage));
        });
    });

    function toggleWhereCanIGoDirection(flightType) {
        if (flightType == 'from') {
            wcigForm.classList.remove('to');
            wcigForm.classList.add('from');
        } else {
            wcigForm.classList.remove('from');
            wcigForm.classList.add('to');
        }
    }
}

function initialiseDatePickerValues() {
    var outRange = document.getElementById("flatpickr-hidden-wcig-out-date")._flatpickr;
    var inRange = document.getElementById("flatpickr-hidden-wcig-in-date")._flatpickr;

    outRange.setDate(wcigLocalStorage['outbound-date-range'], true);
    inRange.setDate(wcigLocalStorage['inbound-date-range'], true);

    outRange.config.onClose.push(function(selectedDates, dateStr, instance) {
        wcigLocalStorage['outbound-date-range'] = selectedDates;
        storeLocalStorage('wcig', JSON.stringify(wcigLocalStorage));
    });

    inRange.config.onClose.push(function(selectedDates, dateStr, instance) {
        wcigLocalStorage['inbound-date-range'] = selectedDates;
        storeLocalStorage('wcig', JSON.stringify(wcigLocalStorage));
    });
}

// ------------------ INITIALISATION ------------------

const data = window.jsData;

if (data.redirect === 'emailDeleteAlert') {
	initialiseDeleteModal(data.deleteAlertFromEmailUrl);
}

init(
    data.newRouteDictionaryAll,
    data.airline ? data.airline : searchBarLocalStorage?.defaultAirline,
    data.subscriber,
    data.airlineSettings,
    data.searchHistoryUrl,
    data.canSearchAvailabilityUrl,
    data.pricingUrl,
    data.popularRoutesUrl,
    data.authenticated,
    data.updatePopularSearches,
    data.popularSearches,
    data.isBot
);